import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';

/**
 * Poll for profile completion status
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether polling should be active
 */
export const useProfilePoll = (accessKey, enabled) => {
  return useQuery({
    queryKey: ['profile-status', accessKey],
    queryFn: async () => {
      const result = await api.checkProfileReady(accessKey);
      if (result?.error) {
        throw new Error(result.message || 'Failed to check profile status');
      }
      return result;
    },
    enabled: enabled && !!accessKey,
    refetchInterval: (data) => {
      // Stop polling if profile is created
      if (data?.status === 'created') {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    refetchIntervalInBackground: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Poll for offerings completion status
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether polling should be active
 */
export const useOfferingsPoll = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['offerings-status', accessKey],
    queryFn: async () => {
      const result = await api.checkOfferingsReady(accessKey);
      if (result?.error) {
        throw new Error(result.message || 'Failed to check offerings status');
      }
      return result;
    },
    enabled: enabled && !!accessKey,
    refetchInterval: (data) => {
      // Stop polling if offerings are ready (array has items)
      if (data && Array.isArray(data) && data.length > 0) {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    refetchIntervalInBackground: true,
    retry: 1,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Poll for posts completion status
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether polling should be active
 */
export const usePostsPoll = (enabled = false) => {
  return useQuery({
    queryKey: ['posts-status'],
    queryFn: async () => {
      const result = await api.checkPostsReady();
      if (result?.error) {
        throw new Error(result.message || 'Failed to check posts status');
      }
      return result;
    },
    enabled: enabled,
    refetchInterval: (data) => {
      // Stop polling if posts are ready and not in the status 'draft'
      const isRegenerating =
        data && Array.isArray(data) && data.some((post) => post.status === 'regeneration');
      if (isRegenerating) {
        return 3000; // Continue polling if any post is regenerating
      }
      if (data && Array.isArray(data) && data.every((post) => post.status !== 'draft')) {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    refetchIntervalInBackground: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    cacheTime: 0,
    staleTime: 0,
  });
};

export const useGetSummeryPoll = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['profile-summary', accessKey],
    queryFn: async () => {
      const result = await api.checkProfileSummaryReady(accessKey);
      if (result?.error) {
        throw new Error(result.message || 'Failed to get profile summary');
      }
      return result;
    },
    enabled: enabled && !!accessKey,
    refetchInterval: (data) => {
      // Stop polling if summary is ready
      if (data && data.summary_ready) {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    refetchIntervalInBackground: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useGetProfileSummary = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['profile-summary', accessKey],
    queryFn: async () => {
      const result = await api.checkProfileSummaryReady(accessKey);
      if (result?.error) {
        throw new Error(result.message || 'Failed to get profile summary');
      }
      return result;
    },
    enabled: enabled && !!accessKey,
    retry: 2,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};

/**
 * Get offerings (single request, no polling)
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether query should be active
 */
export const useGetOfferings = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['offerings', accessKey],
    queryFn: async () => {
      const result = await api.checkOfferingsReady(accessKey);
      if (result?.error) {
        throw new Error(result.message || 'Failed to get offerings');
      }
      return result;
    },
    enabled: enabled && !!accessKey,
    retry: 2,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};

/**
 * Get summary (single request, no polling)
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether query should be active
 */
export const useGetSummary = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['summary', accessKey],
    queryFn: async () => {
      const result = await api.checkProfileSummaryReady(accessKey);
      if (result?.error) {
        throw new Error(result.message || 'Failed to get summary');
      }
      return result;
    },
    enabled: enabled && !!accessKey,
    retry: 2,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};
